import { useEffect, useRef, useState, type ReactNode } from "react";
import { Plus, Search, X, ChevronDown, SlidersHorizontal } from "lucide-react";
import { cn } from "@/cn";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { ViewModeSwitcher } from "./ViewModeSwitcher";
import {
  coerceMobileCollectionViewMode,
  resolveCollectionViewModes,
} from "./view-mode";
import type { ViewMode } from "./view-mode";
import type {
  EntityFilterSelectField,
  EntitySortOption,
} from "./types";

export type CollectionPrimaryAction = {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
};

export type CollectionCommandBarProps = {
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  sortOption?: EntitySortOption;
  onSortChange?: (sort: EntitySortOption) => void;
  sortOptions?: EntitySortOption[];
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  allowedModes?: ViewMode[];
  filterFields?: EntityFilterSelectField[];
  onClear?: () => void;
  activeFilterCount?: number;
  primaryAction?: CollectionPrimaryAction;
  hideViewMode?: boolean;
  className?: string;
};

const inputClass =
  "w-full min-h-[40px] rounded-md border border-border bg-card py-2 pl-9 pr-9 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand";

const selectClass =
  "w-full min-h-[40px] rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand";

/**
 * Collection list command bar.
 * Mobile: Cards|List · Filters (search/sort/fields) · optional Plus.
 * Desktop: search + filters + sort + view modes.
 */
export function CollectionCommandBar({
  searchQuery = "",
  onSearchChange,
  searchPlaceholder = "Search…",
  sortOption,
  onSortChange,
  sortOptions = [],
  viewMode,
  onViewModeChange,
  allowedModes,
  filterFields = [],
  onClear,
  activeFilterCount = 0,
  primaryAction,
  hideViewMode = false,
  className = "",
}: CollectionCommandBarProps) {
  const isDesktop = useIsDesktop();
  const [showFilters, setShowFilters] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);

  const modes = hideViewMode
    ? []
    : resolveCollectionViewModes(
        isDesktop,
        allowedModes ?? ["list", "cards", "table"],
      );
  const displayMode = isDesktop
    ? viewMode
    : coerceMobileCollectionViewMode(viewMode);

  useEffect(() => {
    if (hideViewMode) return;
    if (!isDesktop && displayMode !== viewMode) {
      onViewModeChange(displayMode);
    }
  }, [hideViewMode, isDesktop, displayMode, viewMode, onViewModeChange]);

  useEffect(() => {
    if (!showSortDropdown) return;
    const onDoc = (event: MouseEvent) => {
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target as Node)
      ) {
        setShowSortDropdown(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showSortDropdown]);

  const hasActiveFilters = activeFilterCount > 0 || !!searchQuery;
  const hasSearch = typeof onSearchChange === "function";
  const hasFilterPanel =
    filterFields.length > 0 ||
    hasSearch ||
    (sortOptions.length > 0 && !!onSortChange);

  const filterGridClass =
    filterFields.length <= 1
      ? "grid grid-cols-1 gap-3"
      : filterFields.length === 2
        ? "grid grid-cols-1 gap-3 md:grid-cols-2"
        : "grid grid-cols-1 gap-3 md:grid-cols-3";

  const searchField = hasSearch ? (
    <div className="relative w-full">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={searchPlaceholder}
        className={inputClass}
      />
      {searchQuery ? (
        <button
          type="button"
          onClick={() => onSearchChange("")}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition hover:text-foreground"
          aria-label="Clear search"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  ) : null;

  const sortControl =
    sortOption && onSortChange && sortOptions.length > 0 ? (
      <div className="relative shrink-0" ref={sortDropdownRef}>
        <button
          type="button"
          onClick={() => setShowSortDropdown(!showSortDropdown)}
          className="flex min-h-[40px] items-center gap-1.5 whitespace-nowrap px-2 text-sm text-muted-foreground transition hover:text-foreground"
        >
          <span className="hidden sm:inline">Sort:</span>
          <span className="font-medium text-foreground">{sortOption.label}</span>
          <ChevronDown
            className={cn(
              "h-3 w-3 transition-transform",
              showSortDropdown && "rotate-180",
            )}
          />
        </button>
        {showSortDropdown ? (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowSortDropdown(false)}
            />
            <div className="absolute right-0 z-20 mt-1 w-56 rounded-md border border-border bg-card py-1 shadow-lg">
              {sortOptions.map((option) => {
                const active =
                  sortOption.field === option.field &&
                  sortOption.order === option.order;
                return (
                  <button
                    key={`${option.field}-${option.order}`}
                    type="button"
                    onClick={() => {
                      onSortChange(option);
                      setShowSortDropdown(false);
                    }}
                    className={cn(
                      "flex w-full items-center px-3 py-2 text-left text-sm transition",
                      active
                        ? "bg-brand/10 font-medium text-brand"
                        : "text-muted-foreground hover:bg-muted/20 hover:text-foreground",
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </>
        ) : null}
      </div>
    ) : null;

  const filterFieldsPanel =
    filterFields.length > 0 ? (
      <div className={filterGridClass}>
        {filterFields.map((field) => (
          <div key={field.id}>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              {field.label}
            </label>
            <select
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              className={selectClass}
            >
              {field.allLabel != null ? (
                <option value="">{field.allLabel}</option>
              ) : null}
              {field.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    ) : null;

  const clearButton =
    onClear && hasActiveFilters ? (
      <div className="mt-3 border-t border-border pt-3">
        <button
          type="button"
          onClick={onClear}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-foreground"
        >
          <X className="h-3 w-3" />
          Clear all filters
        </button>
      </div>
    ) : null;

  const filtersButton =
    filterFields.length > 0 || (!isDesktop && hasFilterPanel) || hasActiveFilters ? (
      <button
        type="button"
        onClick={() => setShowFilters(!showFilters)}
        aria-expanded={showFilters}
        className={cn(
          "inline-flex min-h-[40px] shrink-0 items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition",
          showFilters
            ? "border-brand bg-brand text-brand-foreground"
            : hasActiveFilters
              ? "border-border bg-card text-foreground hover:bg-muted/20"
              : "border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted/20",
        )}
      >
        <SlidersHorizontal className="h-3.5 w-3.5" />
        <span>Filters</span>
        {activeFilterCount > 0 ? (
          <span
            className={cn(
              "rounded-full px-1.5 py-0.5 text-xs font-semibold",
              showFilters
                ? "bg-brand-foreground/20 text-brand-foreground"
                : "bg-brand/15 text-brand",
            )}
          >
            {activeFilterCount}
          </span>
        ) : null}
      </button>
    ) : null;

  if (!isDesktop) {
    return (
      <div className={className} data-testid="collection-command-bar-mobile">
        <div className="flex min-w-0 items-center gap-2">
          {!hideViewMode && modes.length > 0 ? (
            <ViewModeSwitcher
              currentMode={displayMode}
              onModeChange={onViewModeChange}
              allowedModes={modes}
              className="shrink-0"
            />
          ) : null}

          {hasFilterPanel ? filtersButton : null}

          {primaryAction ? (
            <button
              type="button"
              onClick={primaryAction.onClick}
              aria-label={primaryAction.label}
              title={primaryAction.label}
              className="ml-auto inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand text-brand-foreground transition hover:opacity-90"
            >
              {primaryAction.icon ?? <Plus className="h-5 w-5" aria-hidden />}
            </button>
          ) : null}
        </div>

        {showFilters && hasFilterPanel ? (
          <div className="mt-3 space-y-3 rounded-md border border-border bg-card p-3">
            {searchField}
            {sortControl ? (
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-muted-foreground">
                  Sort
                </span>
                {sortControl}
              </div>
            ) : null}
            {filterFieldsPanel}
            {clearButton}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={className} data-testid="collection-command-bar-desktop">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        {searchField ? (
          <div className="w-full min-w-0 flex-1 sm:w-auto">{searchField}</div>
        ) : null}

        {filtersButton}

        <div className="ml-auto flex shrink-0 items-center gap-2 sm:border-l sm:border-border sm:pl-3">
          {sortControl}
          {!hideViewMode && modes.length > 0 ? (
            <ViewModeSwitcher
              currentMode={viewMode}
              onModeChange={onViewModeChange}
              allowedModes={modes}
            />
          ) : null}
        </div>
      </div>

      {showFilters && filterFields.length > 0 ? (
        <div className="mt-3 rounded-md border border-border bg-card p-3">
          {filterFieldsPanel}
          {clearButton}
        </div>
      ) : null}
    </div>
  );
}
